import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { AlertTriangle, Phone, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { StatusBadge } from '../../components/StatusBadge';
import { KpiGrid, SectionHeader, ExportButtons, ReportTable, fIDR, fIDRCompact, fDateID } from './shared';

const PRIO_BADGE = {
  High: 'bg-red-400/15 text-red-300 border-red-400/30',
  Medium: 'bg-amber-400/15 text-amber-300 border-amber-400/30',
  Low: 'bg-slate-400/15 text-slate-200 border-slate-400/30',
};

export function OverdueTab({ data, loading, filters }) {
  const navigate = useNavigate();
  const s = data?.summary || {};
  const buckets = s.by_bucket || [];
  const cards = [
    { label: 'Total Overdue', value: fIDRCompact(s.total_overdue), accent: 'danger' },
    { label: 'Jumlah Invoice', value: s.overdue_invoice_count || 0, accent: 'warn' },
    { label: 'Jumlah Client', value: s.overdue_clients_count || 0, accent: '' },
    ...buckets.map(b => ({ label: `Overdue ${b.label}`, value: fIDRCompact(b.amount), accent: b.bucket === '60+ Days' ? 'danger' : 'warn' })),
  ];
  const rows = data?.rows || [];

  return (
    <div className="space-y-4">
      <SectionHeader icon={AlertTriangle} title="Laporan Overdue" action={<ExportButtons tab="overdue" filters={filters} />} />
      <KpiGrid items={cards} loading={loading} columns={4} />
      <Card className="bg-card border-border">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-100">Detail Overdue ({rows.length})</CardTitle></CardHeader>
        <CardContent className="p-0 sm:p-4">
          <ReportTable
            getRowKey={(r) => r.id}
            columns={[
              { key: 'priority', label: 'Priority', render: r => <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${PRIO_BADGE[r.priority] || PRIO_BADGE.Low}`}>{r.priority}</span> },
              { key: 'client_name', label: 'Client', render: r => <span className="font-medium text-slate-100">{r.client_name}</span> },
              { key: 'invoice_number', label: 'No Invoice', render: r => <span className="font-mono-num text-slate-200">{r.invoice_number}</span> },
              { key: 'event_date', label: 'Tgl Event', render: r => <span className="text-slate-300">{fDateID(r.event_date)}</span> },
              { key: 'venue', label: 'Venue', render: r => <span className="text-slate-400" title={r.venue}>{r.venue || '—'}</span> },
              { key: 'due_date', label: 'Jatuh Tempo', render: r => <span className="text-slate-300">{fDateID(r.due_date)}</span> },
              { key: 'aging_days', label: 'Telat', align: 'right', render: r => <span className="text-rose-300 font-medium">{r.aging_days}d</span> },
              { key: 'outstanding_amount', label: 'Outstanding', align: 'right', render: r => <span className="font-mono-num text-amber-200">{fIDR(r.outstanding_amount)}</span> },
              { key: 'last_follow_up_date', label: 'Last FU', render: r => <span className="text-xs text-slate-400">{r.last_follow_up_date ? fDateID(r.last_follow_up_date) : 'Belum'}</span> },
              { key: 'follow_up_status', label: 'Status FU', render: r => <span className="text-xs text-slate-300">{r.follow_up_status}</span> },
              { key: 'responsible_user', label: 'PIC', render: r => <span className="text-xs text-slate-300">{r.responsible_user || '—'}</span> },
              { key: 'action', label: 'Aksi', align: 'right', render: r => (
                <div className="inline-flex gap-1">
                  <Button size="sm" variant="secondary" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/followups?invoice=${r.invoice_id}`); }}><Phone className="h-3 w-3" /></Button>
                  <Button size="sm" variant="secondary" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${r.invoice_id}`); }}><ExternalLink className="h-3 w-3" /></Button>
                </div>
              ) },
            ]}
            rows={rows}
            emptyText="Tidak ada invoice overdue."
            mobileCard={(r) => (
              <div className="text-sm space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${PRIO_BADGE[r.priority] || PRIO_BADGE.Low}`}>{r.priority}</span>
                      <span className="font-semibold text-slate-100 truncate">{r.client_name}</span>
                    </div>
                    <div className="text-xs text-slate-300 font-mono-num">{r.invoice_number}</div>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
                <div className="grid grid-cols-2 gap-1 text-xs text-slate-300 mt-2">
                  <div>Due: {fDateID(r.due_date)}</div>
                  <div>Telat: <span className="text-rose-300 font-medium">{r.aging_days}d</span></div>
                  <div>Last FU: {r.last_follow_up_date ? fDateID(r.last_follow_up_date) : 'Belum'}</div>
                  <div>Status FU: {r.follow_up_status}</div>
                </div>
                <div className="font-mono-num font-semibold text-amber-200 mt-1">{fIDR(r.outstanding_amount)}</div>
              </div>
            )}
          />
        </CardContent>
      </Card>
    </div>
  );
}
